import Decimal, {DecimalSource} from './break_infinity';
import {html, LitElement, TemplateResult} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {ri} from './runner-idle';
import {
  of,
  BehaviorSubject,
  Observable,
  map,
  Subscription,
  NEVER,
  startWith,
} from 'rxjs';
import {DynamicCost, evaluateCost} from './interfaces/dynamic-value';
import {RxLitElement} from './rx-lit-element';
import {capitalizeFirstLetter, formatCostEffect, formatTime} from './utils';
import {Stat} from './stat';
import {IValuable} from './interfaces/valuable';
import {Gettable} from './types/runner-idle';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
@customElement('ri-status-effect')
export class StatusEffect extends RxLitElement {
  displayName = 'Status Effect';
  @state() timeLeft: Decimal = new Decimal(10);
  maxTimeLeft: Decimal = new Decimal(3600);
  interval: Decimal = new Decimal(1);
  /**
   * Backing property for this.interval$.
   * Returned upon get
   * and subscribes to values set
   */
  readonly #persistentInterval$: BehaviorSubject<Decimal> = new BehaviorSubject(
    new Decimal(1)
  );
  /** Subscription for pass-through of values via #persistentInterval$ to interval$ */
  #intervalSubscription?: Subscription;
  /** The base stream for this.interval$. Holds the raw value assigned via set this.interval$ */
  protected baseInterval$: Observable<Decimal> = NEVER;
  /**
   * A stream of values describing whether this is currently interval.
   * Getting this returns a persistent Observable, and new set values are passed to that stream.
   * i.e. You can do `let newVar = activity.interval$` and it will always be up to date,
   * even if later you do `activity.interval$ = new Observable()`.
   */
  public get interval$(): Observable<Decimal> {
    return this.#persistentInterval$;
  }
  public set interval$(value: Observable<Decimal>) {
    if (!value) return;
    this.baseInterval$ = value; // Sometimes I pipe value through something first, hence
    this.#intervalSubscription?.unsubscribe();
    this.#intervalSubscription = this.baseInterval$.subscribe((unlocked) =>
      this.#persistentInterval$.next(unlocked)
    );
  }
  intervalTimeLeft: Decimal = new Decimal(1);
  performAtStart = false;
  performAtEnd = false;
  cost$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  cost!: DynamicCost;
  effect$: Observable<DynamicCost> = new BehaviorSubject(new Map());
  effect!: DynamicCost;
  stacks: Decimal = new Decimal(1);
  maxStacks: Decimal = new Decimal(1);

  perform(deltaTimeSec: Decimal) {
    // console.log(
    //   this.displayName,
    //   'perform',
    //   'deltaTimeSec',
    //   deltaTimeSec.toString(),
    //   'timeLeft',
    //   this.timeLeft.toString(),
    //   'intervalTimeLeft',
    //   this.#intervalTimeLeft.toString()
    // );
    const evaluatedCost = evaluateCost(this.cost, 0, 0, {});
    const evaluatedEffect = evaluateCost(this.effect, 0, 0, {});
    // Can we afford the cost? Then apply the effect
    const costValuables = [...evaluatedCost.keys()].map(ri.get.bind(ri));
    if (
      costValuables.every((valuable) =>
        valuable?.value.gte(evaluatedCost.get(valuable.name).value!)
      )
    ) {
      while (this.timeLeft.gt(0) && deltaTimeSec.gte(this.intervalTimeLeft)) {
        deltaTimeSec = deltaTimeSec.minus(this.intervalTimeLeft);
        this.timeLeft = this.timeLeft.minus(this.intervalTimeLeft);
        this.intervalTimeLeft = this.interval;
        for (const [key, v] of evaluatedCost) {
          ri.gain(key, new Decimal(v.value).negate());
        }
        for (const [key, v] of evaluatedEffect) {
          ri.gain(key, v.value);
        }
      }
    }
    this.timeLeft = this.timeLeft.minus(deltaTimeSec);
    this.intervalTimeLeft = this.intervalTimeLeft.minus(deltaTimeSec);
    if (this.timeLeft.lte(0)) {
      this.onEnd();
      ri.removeStatusEffect(this);
    }
  }
  onStart() {
    // console.log(this.displayName, 'onStart');
  }
  onEnd() {
    // console.log(this.displayName, 'onEnd');
  }
  setup(overrides: Partial<StatusEffect>) {
    Object.assign(this, overrides);
    this.subscribe('cost', this.cost$);
    this.subscribe('effect', this.effect$);
    this.subscribe('interval', this.interval$);
  }

  protected override render(): TemplateResult {
    return html`<div>
      ${this.displayName} ${formatTime(this.timeLeft)} left, every
      ${formatTime(this.interval)}
      ${unsafeHTML(
        formatCostEffect({cost: this.cost, effect: this.effect}, 1, true)
      )}
    </div>`;
  }

  static create(template: Partial<StatusEffect>): StatusEffect {
    const instance = document.createElement('ri-status-effect');
    instance.setup(template);
    return instance;
  }
}

export const makeBurnTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> => {
  return Object.assign(
    {
      displayName: 'On Fire',
      effect$: of(
        new Map([
          [
            'health',
            {
              value: () => {
                const stacks = new Decimal(
                  ri.statusEffects.find((s) => s.displayName === 'On Fire')
                    ?.stacks ?? 1
                );
                return stacks.negate();
                // if (stacks.lt(10)) return new Decimal(-1);
                // return stacks.div(10).plus(5).negate();
              },
            },
          ],
        ])
      ),
      timeLeft: new Decimal(10),
      maxTimeLeft: new Decimal(30),
      interval$: ri.tick$.pipe(
        map(
          () =>
            ri.statusEffects
              .find((s) => s.displayName === 'On Fire')
              ?.stacks.negate()
              .plus(11)
              .max(1) ?? new Decimal(1)
        )
      ),
      maxStacks: new Decimal(100),
    },
    overrides
  );
};

export const makePoisonTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> => {
  const originalHealthBaseCapacity = ri.resources.health.baseCapacity;
  return Object.assign(
    {
      displayName: 'Poisoned',
      effect$: of(
        new Map([
          [
            'health:poison:capacityReduction',
            {
              value: () => 1,
            },
          ],
        ])
      ),
      timeLeft: new Decimal(60),
      interval: new Decimal(10),
      onEnd() {
        ri.resources.health.removeCapacityReduction('poison');
      },
    },
    overrides
  );
};

export const makeBlindTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> => {
  const originalPerception = ri.stats.perception.value;
  return Object.assign(
    {
      displayName: 'Blinded',
      effect$: of(
        new Map([
          [
            'perception',
            {
              value: () => originalPerception.minus(-9).negate(),
            },
          ],
        ])
      ),
      interval: new Decimal(10),
      timeLeft: new Decimal(9),
      onEnd() {
        ri.stats.perception.setValue(
          originalPerception.plus(ri.stats.perception.value.minus(-9))
        );
      },
    },
    overrides
  );
};

export const makeConfusionTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> =>
  Object.assign(
    {
      displayName: 'Confused',
      effect$: of(
        new Map([
          ['targetX', {value: () => 5 * (Math.random() - 0.5)}],
          ['targetY', {value: () => 5 * (Math.random() - 0.5)}],
        ])
      ),
      timeLeft: new Decimal(10),
    },
    overrides
  );

export const makeDiscoFeverTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> =>
  Object.assign({
    displayName: 'Disco Fever',
    effect$: of(
      new Map([
        ['targetX', {value: () => 2 * (Math.random() - 0.5)}],
        ['targetY', {value: () => 2 * (Math.random() - 0.5)}],
        ['distance', {value: () => 1}],
        [
          'backgroundHue',
          {
            value: () => Math.random() * 255,
          },
        ],
      ])
    ),
    timeLeft: new Decimal(60),
  });

export const makeRegenerationTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> =>
  Object.assign(
    {
      displayName: 'Regeneration',
      effect$: of(
        new Map([
          ['health', {value: () => 1}],
          ['stamina', {value: () => 1}],
        ])
      ),
    },
    overrides
  );

export const makeNorthboundCurseTemplate = (
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> =>
  Object.assign(
    {
      displayName: 'Northbound Curse (Curse of the Waste?)',
      effect$: of(
        new Map([
          [
            'targetY',
            {
              value: () => {
                const deltaX = ri.position.x.minus(ri.targetPosition.x);
                const deltaY = ri.position.y.minus(ri.targetPosition.y);
                if (deltaX.eq(0)) {
                  return 1;
                }
                return deltaY.abs().div(deltaX.abs()).times(2).plus(10);
              },
            },
          ],
          [
            'targetX',
            {
              value: () => {
                const deltaX = ri.position.x.minus(ri.targetPosition.x);
                return deltaX.abs().min(1).times(deltaX.sign());
              },
            },
          ],
        ])
      ),
      timeLeft: new Decimal(300),
    },
    overrides
  );

export const makeWitherTemplate = (
  gettable: Gettable = 'stamina',
  overrides: Partial<StatusEffect> = {}
): Partial<StatusEffect> =>
  Object.assign(
    {
      name: `wither${capitalizeFirstLetter(gettable)}`,
      displayName: `Wither ${capitalizeFirstLetter(gettable)}`,
      cost$: of(new Map([[`${gettable}:level`, {value: () => 1}]])),
      effect$: of(new Map([['witherMagic', {value: () => 0.1}]])),
      timeLeft: new Decimal(300),
      interval: new Decimal(60),
      // interval: new Decimal(10),
    },
    overrides
  );
